import { useState, useEffect, type ReactNode } from "react";
import { Logo } from "@/components/Logo";
import { Mail, Send, Loader2, CheckCircle2, ShieldOff } from "lucide-react";
import { useToast } from "@/hooks/use-toast";
import {
  auth,
  sendAdminMagicLink,
  completeAdminSignIn,
  adminFetch,
} from "@/lib/adminAuth";
import { onAuthStateChanged } from "firebase/auth";

type GateState = "checking" | "not-signed-in" | "sending" | "link-sent" | "completing" | "not-admin" | "ready";

interface AdminLoginGateProps {
  children: ReactNode;
  label?: string;
}

export function AdminLoginGate({ children, label = "Admin" }: AdminLoginGateProps) {
  const { toast } = useToast();
  const [state, setState] = useState<GateState>("checking");
  const [email, setEmail] = useState("");

  const checkAdminStatus = async () => {
    const token = await auth.currentUser?.getIdToken();
    if (!token) { setState("not-signed-in"); return; }
    try {
      const res = await adminFetch("/api/blogs/is-admin");
      const data = await res.json();
      setState(data.isAdmin ? "ready" : "not-admin");
    } catch {
      setState("not-signed-in");
    }
  };

  useEffect(() => {
    const tryComplete = async () => {
      setState("completing");
      try {
        const completed = await completeAdminSignIn();
        if (completed) {
          await checkAdminStatus();
          return;
        }
      } catch (e: any) {
        toast({ title: "Sign-in failed", description: e.message, variant: "destructive" });
      }
      const unsubscribe = onAuthStateChanged(auth, async (user) => {
        unsubscribe();
        if (user) {
          await checkAdminStatus();
        } else {
          setState("not-signed-in");
        }
      });
    };
    tryComplete();
  }, []);

  const handleSendLink = async () => {
    if (!email.trim()) return;
    setState("sending");
    try {
      await sendAdminMagicLink(email.trim());
      setState("link-sent");
    } catch (e: any) {
      toast({ title: "Failed to send link", description: e.message, variant: "destructive" });
      setState("not-signed-in");
    }
  };

  if (state === "checking" || state === "completing") {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#FFFBF2] dark:bg-[#1A1614]">
        <Loader2 className="h-6 w-6 animate-spin text-[#8B2121]" />
      </div>
    );
  }

  if (state === "not-admin") {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#FFFBF2] dark:bg-[#1A1614] px-6">
        <div className="text-center">
          <ShieldOff className="h-10 w-10 text-slate-300 mx-auto mb-3" />
          <p className="text-xl font-bold text-slate-900 dark:text-white mb-2">Access Denied</p>
          <p className="text-slate-500 text-sm">This account doesn't have admin access.</p>
          <button
            onClick={() => { auth.signOut(); setState("not-signed-in"); }}
            className="mt-4 text-xs text-slate-400 hover:text-[#8B2121] transition-colors"
          >
            Sign out and try a different email
          </button>
        </div>
      </div>
    );
  }

  if (state === "link-sent") {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#FFFBF2] dark:bg-[#1A1614] px-6">
        <div className="text-center max-w-xs">
          <div className="w-14 h-14 rounded-full bg-green-50 dark:bg-green-950/30 flex items-center justify-center mx-auto mb-4">
            <CheckCircle2 className="h-7 w-7 text-green-600" />
          </div>
          <p className="text-xl font-bold text-slate-900 dark:text-white mb-2">Check your email</p>
          <p className="text-slate-500 text-sm leading-relaxed">
            A sign-in link has been sent to <span className="font-semibold text-slate-700 dark:text-slate-300">{email}</span>. Click it to log in — no password needed.
          </p>
          <button
            onClick={() => setState("not-signed-in")}
            className="mt-5 text-xs text-slate-400 hover:text-[#8B2121] transition-colors"
          >
            Use a different email
          </button>
        </div>
      </div>
    );
  }

  if (state === "not-signed-in" || state === "sending") {
    return (
      <div className="min-h-screen bg-[#FFFBF2] dark:bg-[#1A1614]">
        <header className="px-4 py-3 border-b border-[#8B2121]/10 dark:border-white/10">
          <div className="max-w-sm mx-auto">
            <Logo size="md" />
          </div>
        </header>
        <div className="flex items-center justify-center min-h-[calc(100vh-57px)] px-6">
          <div className="w-full max-w-xs">
            <div className="text-center mb-7">
              <div className="w-14 h-14 rounded-2xl bg-[#8B2121]/10 flex items-center justify-center mx-auto mb-4">
                <Mail className="h-7 w-7 text-[#8B2121]" />
              </div>
              <h1 className="text-xl font-bold text-slate-900 dark:text-white">{label} Login</h1>
              <p className="text-slate-500 text-sm mt-1">Enter your email to receive a magic sign-in link</p>
            </div>

            <div className="space-y-3">
              <input
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                onKeyDown={(e) => e.key === "Enter" && handleSendLink()}
                placeholder="your@email.com"
                data-testid="input-admin-email"
                autoFocus
                className="w-full h-12 px-4 rounded-2xl bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-700 text-sm font-medium text-slate-800 dark:text-white placeholder:text-slate-400 focus:outline-none focus:ring-2 focus:ring-[#8B2121]/30"
              />
              <button
                onClick={handleSendLink}
                disabled={state === "sending" || !email.trim()}
                data-testid="button-send-magic-link"
                className="w-full h-12 rounded-2xl text-white text-sm font-bold flex items-center justify-center gap-2 active:scale-[0.98] transition-transform disabled:opacity-60"
                style={{ background: "linear-gradient(135deg, #8B1A1A 0%, #631212 100%)" }}
              >
                {state === "sending" ? (
                  <><Loader2 className="h-4 w-4 animate-spin" /> Sending...</>
                ) : (
                  <><Send className="h-4 w-4" /> Send Magic Link</>
                )}
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return <>{children}</>;
}
